import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import type { ScreenId } from '../../app/screenRoutes';
import { navigateToLandingSection } from '../../config/navigation';
import { customerCabinetApi } from '../../features/customerCabinet/api';
import { useFigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { useAuth } from '../../state/auth';
import { useChatStatus } from '../../state/chatStatus';
import {
  CabinetSidebar,
  Footer,
  Header,
  MessageIcon,
  MobileDrawer,
  NotificationBell,
  type MobileDrawerAction,
} from '../../ui';
import './customer-cabinet-layout.css';

export type CustomerCabinetScreenId = Extract<ScreenId,
  | 'my-requests'
  | 'request-detail'
  | 'payment'
  | 'active-transport'
  | 'chat-list'
  | 'chat'
  | 'review'
  | 'dispute'
  | 'notifications'
  | 'settings'
  | 'profile-internal'
  | 'profile-external'
  | 'platform-feedback'
  | 'customer-help'
>;

export type CustomerFigmaNodes = Record<'1920' | '1440' | '768' | '375', string | null>;

const SIDEBAR_GROUPS: Array<{
  title: string;
  items: Array<{ screen: CustomerCabinetScreenId; label: string; icon: string }>;
}> = [
  {
    title: 'Кабинет',
    items: [
      { screen: 'my-requests', label: 'Мои заявки', icon: 'carrier-sidebar-orders.svg' },
      { screen: 'chat-list', label: 'Сообщения', icon: 'carrier-sidebar-messages.svg' },
      { screen: 'notifications', label: 'Уведомления', icon: 'carrier-sidebar-bell.svg' },
      { screen: 'active-transport', label: 'Активные перевозки', icon: 'carrier-sidebar-car.svg' },
    ],
  },
  {
    title: 'Аккаунт',
    items: [
      { screen: 'profile-internal', label: 'Профиль', icon: 'carrier-sidebar-profile.svg' },
      { screen: 'settings', label: 'Настройки', icon: 'carrier-sidebar-settings.svg' },
      { screen: 'customer-help', label: 'Помощь', icon: 'carrier-sidebar-help.svg' },
    ],
  },
];

function go(screen: string, params?: Record<string, string | number>) {
  const query = params
    ? `?${new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)])).toString()}`
    : '';
  window.location.hash = `${screen}${query}`;
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

const SCREEN_TO_DRAWER_ACTION: Partial<Record<CustomerCabinetScreenId, MobileDrawerAction>> = {
  'my-requests': 'owner-requests',
  'request-detail': 'owner-requests',
  payment: 'owner-requests',
  'chat-list': 'owner-messages',
  chat: 'owner-messages',
  notifications: 'owner-notifications',
  'active-transport': 'owner-active-transports',
  'profile-internal': 'owner-profile',
  'profile-external': 'owner-profile',
  settings: 'owner-settings',
  'customer-help': 'help',
};

const DRAWER_ROUTES: Partial<Record<MobileDrawerAction, CustomerCabinetScreenId | 'landing'>> = {
  logo: 'landing',
  'personal-cabinet': 'my-requests',
  'owner-requests': 'my-requests',
  'owner-messages': 'chat-list',
  'owner-notifications': 'notifications',
  'owner-active-transports': 'active-transport',
  'owner-profile': 'profile-internal',
  'owner-settings': 'settings',
  help: 'customer-help',
};

interface CustomerCabinetLayoutProps {
  children: ReactNode;
  className?: string;
  contentHeight: Record<'desktop' | 'tablet' | 'mobile', number>;
  figmaNode: CustomerFigmaNodes;
  screen: CustomerCabinetScreenId;
  topOffset?: Record<'desktop' | 'tablet' | 'mobile', number>;
}

export function CustomerCabinetLayout({
  children,
  className = '',
  contentHeight,
  figmaNode,
  screen,
  topOffset = { desktop: 0, tablet: 0, mobile: 0 },
}: CustomerCabinetLayoutProps) {
  const breakpoint = useFigmaBreakpoint();
  const auth = useAuth();
  const chatStatus = useChatStatus();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [displayName, setDisplayName] = useState(auth.user?.display_name || auth.user?.email.split('@')[0] || '');

  useEffect(() => {
    let active = true;
    if (auth.user?.display_name) {
      setDisplayName(auth.user.display_name);
    }
    customerCabinetApi.profile()
      .then((profile) => { if (active && profile.display_name) setDisplayName(profile.display_name); })
      .catch(() => {});
    return () => { active = false; };
  }, [auth.user?.display_name]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [screen]);

  const style = {
    '--customer-desktop-height': `${contentHeight.desktop}px`,
    '--customer-tablet-height': `${contentHeight.tablet}px`,
    '--customer-mobile-height': `${contentHeight.mobile}px`,
    '--customer-desktop-offset': `${topOffset.desktop}px`,
    '--customer-tablet-offset': `${topOffset.tablet}px`,
    '--customer-mobile-offset': `${topOffset.mobile}px`,
  } as CSSProperties;

  function navigateDrawer(action: MobileDrawerAction) {
    if (action === 'how-it-works') navigateToLandingSection('how');
    if (action === 'why-lovo' || action === 'reviews') navigateToLandingSection('reviews');
    const route = DRAWER_ROUTES[action];
    if (route) go(route);
    setDrawerOpen(false);
  }

  const activeDrawerAction = SCREEN_TO_DRAWER_ACTION[screen];

  return (
    <div
      className={['customer-cabinet', `customer-cabinet--${screen}`, className].filter(Boolean).join(' ')}
      data-screen={screen}
      data-figma-node={figmaNode[breakpoint]}
      data-figma-variant={`Size=${breakpoint}`}
      style={style}
    >
      <Header
        authenticated
        actions={(
          <div className="customer-cabinet__header-actions">
            <a
              href="#chat-list"
              aria-label="Сообщения"
              onClick={(e) => {
                e.preventDefault();
                go('chat-list');
              }}
            >
              <MessageIcon hasUnread={chatStatus.unreadMessages > 0} />
              {chatStatus.unreadMessages > 0 ? <span className="cabinet-header-count">{chatStatus.unreadMessages > 99 ? '99+' : chatStatus.unreadMessages}</span> : null}
            </a>
            <a
              href="#notifications"
              aria-label="Уведомления"
              onClick={(e) => {
                e.preventDefault();
                go('notifications');
              }}
            >
              <NotificationBell hasUnread={chatStatus.unreadNotifications > 0} />
              {chatStatus.unreadNotifications > 0 ? <span className="cabinet-header-count">{chatStatus.unreadNotifications > 99 ? '99+' : chatStatus.unreadNotifications}</span> : null}
            </a>
          </div>
        )}
        account={(
          <a
            className="lovo-header__account-btn"
            href="#profile-internal"
            onClick={(e) => {
              e.preventDefault();
              go('profile-internal');
            }}
            data-figma-node="61:21746"
          >
            <span>{displayName}</span>
            <img src="/figma/assets/profile-arrow-up-right.svg" alt="" aria-hidden="true" />
          </a>
        )}
        onLogoClick={() => go('landing')}
        onMenuToggle={() => setDrawerOpen(true)}
        menuExpanded={drawerOpen}
        externalDrawer
      />

      {drawerOpen ? (
        <MobileDrawer
          variant="owner"
          activeAction={activeDrawerAction}
          onClose={() => setDrawerOpen(false)}
          onLogoClick={() => go('landing')}
          onNavigate={navigateDrawer}
        />
      ) : null}

      <div className="customer-cabinet__workspace">
        <CabinetSidebar
          groups={SIDEBAR_GROUPS}
          activeScreen={screen}
          activeLabel={['my-requests', 'request-detail', 'payment'].includes(screen) ? 'Мои заявки' : undefined}
          onNavigate={(targetScreen) => {
            if ((targetScreen as string) === 'landing') return;
            go(targetScreen);
          }}
          unreadMessages={chatStatus.unreadMessages}
          unreadNotifications={chatStatus.unreadNotifications}
        />

        <main className="customer-cabinet__main">{children}</main>
      </div>

      <Footer
        size={breakpoint}
        legalDetails={{
          legalName: 'ООО/ИП “ХХХ”',
          inn: '123456789012',
          ogrnip: '12345678901234567',
          year: 2026,
        }}
        onNavigate={(action) => {
          if (action === 'logo') go('landing');
          if (action === 'customer-cabinet') go('my-requests');
          if (action === 'carrier-cabinet') go('carrier-dashboard');
          if (action === 'carrier-registration') go('carrier-rega-email');
        }}
      />
    </div>
  );
}

export function navigateCustomer(screen: CustomerCabinetScreenId, params?: Record<string, string | number>) {
  go(screen, params);
}
